import React, { useState, useEffect, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Session } from "@supabase/supabase-js";
import { supabase } from "./services/supabase";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { SpinnerFallback } from "./components/SpinnerFallback";
import { LanguageProvider } from "./contexts/LanguageContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Layout } from "./components/Layout";
import { BetaBanner } from "./components/BetaBanner";
import { AuthRedirectHandler } from "./components/AuthRedirectHandler";
import { UpgradeModalProvider } from "./components/UpgradeModal";
import {
  initErrorTracking,
  setErrorTrackingUser,
  clearErrorTrackingUser,
} from "./services/errorTrackingService";
import {
  initAnalytics,
  identifyUser,
  resetAnalyticsUser,
  trackPageView,
  trackEvent,
  ANALYTICS_EVENTS,
} from "./services/productAnalyticsService";
import { RecoveryProvider } from "./features/recovery/context/RecoveryContext";
import { RecoveryFocusView } from "./features/recovery/components/RecoveryFocusView";

const AuthPage = React.lazy(() =>
  import("./components/AuthPage").then((m) => ({ default: m.AuthPage })),
);
const AuthCallbackPage = React.lazy(() => import("./pages/AuthCallbackPage"));
const ForgotPasswordPage = React.lazy(
  () => import("./pages/ForgotPasswordPage"),
);
const ResetPasswordPage = React.lazy(() => import("./pages/ResetPasswordPage"));
const Dashboard = React.lazy(() =>
  import("./components/Dashboard").then((m) => ({ default: m.Dashboard })),
);

const AnalyticsPage = React.lazy(
  () => import("@/features/analytics/components/AnalyticsPage"),
);
const GradesPage = React.lazy(() => import("./pages/GradesPage"));
const ProjectTeamsPage = React.lazy(
  () => import("@/features/studyGroups/components/ProjectTeamsPage"),
);
const LeaderboardsPage = React.lazy(() => import("./pages/LeaderboardsPage"));
const AIPlannerPage = React.lazy(() => import("./pages/AIPlannerPage"));

const LandingPage = React.lazy(() => import("./pages/LandingPage"));
const PrivacyPolicyPage = React.lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage = React.lazy(() => import("./pages/TermsOfServicePage"));

import { SEOHead } from "./components/SEOHead";

// Initialize error tracking and analytics on app load
initErrorTracking();
initAnalytics();

// Route change tracker component
const RouteTracker: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  return null;
};

const App: React.FC = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        setSession(session);
        setLoading(false);

        // Set user context for error tracking and analytics
        if (session?.user) {
          setErrorTrackingUser({
            userId: session.user.id,
            email: session.user.email,
          });
          identifyUser({
            userId: session.user.id,
            email: session.user.email,
            signupDate: session.user.created_at,
          });
          trackEvent(ANALYTICS_EVENTS.LOGIN_COMPLETED);
        }
      })
      .catch((err) => {
        console.error("Supabase Session Error:", err);
        setLoading(false); // Ensure we don't get stuck loading
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);

      // Update tracking context on auth changes
      if (session?.user) {
        setErrorTrackingUser({
          userId: session.user.id,
          email: session.user.email,
        });
        identifyUser({
          userId: session.user.id,
          email: session.user.email,
        });
      } else {
        clearErrorTrackingUser();
        resetAnalyticsUser();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-yellow-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
        <p className="ml-4 text-xl text-gray-600">Loading your desk...</p>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <RecoveryProvider>
          <LanguageProvider>
            <UpgradeModalProvider>
              <Router>
                <RouteTracker />
                <SEOHead />
                <Suspense fallback={<SpinnerFallback label="Loading Page" />}>
                  <div className="flex flex-col min-h-screen">
                    <AuthRedirectHandler />
                    <BetaBanner />
                    <RecoveryFocusView />
                    <Routes>
                      <Route path="/" element={<LandingPage />} />
                      <Route path="/privacy" element={<PrivacyPolicyPage />} />
                      <Route path="/terms" element={<TermsOfServicePage />} />
                      <Route
                        path="/auth/callback"
                        element={<AuthCallbackPage />}
                      />
                      <Route
                        path="/forgot-password"
                        element={<ForgotPasswordPage />}
                      />
                      <Route
                        path="/reset-password"
                        element={<ResetPasswordPage />}
                      />
                      <Route
                        path="/login"
                        element={
                          !session ? (
                            <AuthPage />
                          ) : (
                            <Navigate to="/dashboard" replace />
                          )
                        }
                      />

                      {/* Authenticated Routes wrapped in Layout */}
                      {session && (
                        <Route element={<Layout />}>
                          <Route path="/dashboard" element={<Dashboard />} />

                          <Route
                            path="/analytics"
                            element={<AnalyticsPage />}
                          />
                          <Route path="/grades" element={<GradesPage />} />
                          <Route
                            path="/projects"
                            element={<ProjectTeamsPage />}
                          />
                          <Route
                            path="/leaderboards"
                            element={<LeaderboardsPage />}
                          />
                          <Route
                            path="/ai-planner"
                            element={<AIPlannerPage />}
                          />
                        </Route>
                      )}

                      {/* Catch-all redirect */}
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </div>
                </Suspense>
              </Router>
            </UpgradeModalProvider>
          </LanguageProvider>
        </RecoveryProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
